import { Button, IconButton } from "@ai-teach/ui-web";
import { useQuery } from "@apollo/client/react";
import { useSearch } from "@tanstack/react-router";
import {
  AnalyticsSchoolsDocument,
  AnalyticsStudentsDocument,
  AnalyticsTeachersDocument,
} from "../../../generated/graphql";
import { useFilterUpdates } from "./-useFilterUpdates";
import { useUserRole } from "./-useUserRole";

export function FilterPanel() {
  const filters = useSearch({ from: "/admin/analytics/" });
  const { updateFilter } = useFilterUpdates();
  const { role, permissions } = useUserRole();

  // Fetch dropdown options
  const { data: schoolsData } = useQuery(AnalyticsSchoolsDocument);
  const { data: teachersData } = useQuery(AnalyticsTeachersDocument, {
    variables: { schoolId: filters.school || "" },
    skip: !filters.school,
  });
  const { data: studentsData } = useQuery(AnalyticsStudentsDocument, {
    variables: { schoolId: filters.school || "" },
    skip: !filters.school,
  });

  // Filter teachers only
  const filteredTeachers = teachersData?.usersBySchoolId
    ?.filter((user) => user.role === "TEACHER" || user.role === "PRINCIPAL")
    .map((user) => ({
      ...user,
      displayName: `${user.username} (${user.role.toLowerCase()})`,
    }));

  // Filter students only
  const filteredStudents = studentsData?.usersBySchoolId
    ?.filter((user) => user.role === "STUDENT")
    .map((user) => ({
      ...user,
      displayName: user.username,
    }));

  // Check if any filters are active
  const hasActiveFilters = filters.school || filters.teacher || filters.student;

  // Clear all filters
  const clearAllFilters = () => {
    updateFilter("school", undefined);
    updateFilter("teacher", undefined);
    updateFilter("student", undefined);
  };

  return (
    <div className="panel p-4 mb-6">
      <div className="flex items-center justify-between">
        <h3 className="h6">Filters (Role: {role})</h3>
        {hasActiveFilters && (
          <Button type="button" onClick={clearAllFilters} variant="secondary">
            Clear Filters
          </Button>
        )}
      </div>

      <div className="flex gap-4 mt-3">
        {/* School Filter - only if permitted */}
        {permissions.canFilterSchools && (
          <div>
            <label className="label">School:</label>
            <select
              value={filters.school || ""}
              onChange={(e) => {
                updateFilter("school", e.target.value || undefined);
                // Reset teacher and student when school changes
                if (filters.teacher) {
                  updateFilter("teacher", undefined);
                }
                if (filters.student) {
                  updateFilter("student", undefined);
                }
              }}
              className="ml-2 px-2 py-1 bg-white dark:bg-surface border border-gray-300 dark:border-gray-800 rounded-md text-gray-900 dark:text-secondary-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Schools</option>
              {schoolsData?.accessibleSchools?.map((school) => (
                <option key={school.id} value={school.id}>
                  {school.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Teacher Filter - only if permitted and school selected */}
        {permissions.canFilterStudents && (
          <div>
            <label className="label">Teacher:</label>
            <select
              value={filters.teacher || ""}
              onChange={(e) => {
                updateFilter("teacher", e.target.value || undefined);
                // Reset student when teacher changes
                if (filters.student) {
                  updateFilter("student", undefined);
                }
              }}
              disabled={!filters.school}
              className="ml-2 px-2 py-1 bg-white dark:bg-surface border border-gray-300 dark:border-gray-800 rounded-md text-gray-900 dark:text-secondary-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">
                {filters.school
                  ? filteredTeachers?.length === 0
                    ? "No teachers in this school"
                    : "All teachers"
                  : "Select a school first"}
              </option>
              {filteredTeachers?.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.displayName}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Student Filter - only if permitted */}
        {permissions.canFilterStudents && (
          <div>
            <label className="label">Student:</label>
            <select
              value={filters.student || ""}
              onChange={(e) => updateFilter("student", e.target.value || undefined)}
              disabled={!filters.school}
              className="ml-2 px-2 py-1 bg-white dark:bg-surface border border-gray-300 dark:border-gray-800 rounded-md text-gray-900 dark:text-secondary-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">
                {filters.school
                  ? filteredStudents?.length === 0
                    ? "No students in this school"
                    : filters.teacher
                      ? "All students for this teacher"
                      : "All students in this school"
                  : "Select a school first"}
              </option>
              {filteredStudents?.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.displayName}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {permissions.lockedToOwnData && (
        <div className="mt-3 text-error-500 text-sm">ℹ️ You can only view your own data</div>
      )}

      <div className="mt-3 caption">
        Current URL params:{" "}
        {typeof window !== "undefined" ? window.location.search || "(none)" : "(SSR)"}
      </div>

      {/* Filter Summary Title */}
      <div className="mt-3 text-base font-semibold text-gray-900 dark:text-secondary-200">
        {filters.student ? (
          <>
            Viewing:{" "}
            {filteredStudents?.find((s) => s.id === filters.student)?.displayName || "Student"}
          </>
        ) : filters.teacher ? (
          <>
            Viewing:{" "}
            {filteredTeachers?.find((t) => t.id === filters.teacher)?.displayName || "Teacher"}'s
            Students
          </>
        ) : filters.school ? (
          <>
            Viewing:{" "}
            {schoolsData?.accessibleSchools?.find((s) => s.id === filters.school)?.name || "School"}{" "}
            - All Students & Teachers
          </>
        ) : (
          <>Viewing: All Students & Teachers (All Schools)</>
        )}
      </div>
    </div>
  );
}
